import { ChooseTreatmentProductType, ChooseTreatmentProductItemType, AdditionalData, CannabisLimitsResult, CannabisLimitViolation, SortByType, SORT_BY_PRICE_ASC, SORT_BY_PRICE_DESC, SORT_BY_THC_ASC, SORT_BY_THC_DESC } from "./types";
import { translate } from '../../utils/translator';


type ProductWithStandardItems<T extends ChooseTreatmentProductType = ChooseTreatmentProductType> =
  Omit<T, 'items'> & { items: ChooseTreatmentProductItemType[] };

/**
 * Formats a THC or CBD value with its unit, applying the cutting rule for percentage units.
 * The cutting rule shows "<1" when the value is less than 1 and the unit is "%".
 * 
 * @param value - The THC or CBD numeric value
 * @param unit - The unit string ("%", "mg", or "ml"), defaults to "%" if not provided
 * @returns Formatted string (e.g., "25%", "<1%", "10mg", "0.5ml")
 */
export const formatCannabisValueWithUnit = (value: number, unit?: string | null): string => {
  const displayUnit = unit || "%";
  
  // Apply cutting rule: show "<1" only if unit is "%" and value is less than 1
  if (displayUnit === "%" && value < 1) {
    return `<1${displayUnit}`;
  }
  
  return `${value}${displayUnit}`;
};

/**
 * Formats THC and CBD values with their respective units and returns formatted display strings.
 * 
 * @param additionalData - The additional data object containing THC, CBD, and their units
 * @returns Object with formatted thcDisplay, cbdDisplay, and strain string (if applicable)
 */
export const formatCannabisDisplayData = (additionalData: AdditionalData | null | undefined): {
  thcDisplay: string;
  cbdDisplay: string;
  strain: string;
  shouldUseCannabisDosage: boolean;
} => {
  if (!additionalData || !additionalData.thc || !additionalData.cbd) {
    return {
      thcDisplay: "",
      cbdDisplay: "",
      strain: "",
      shouldUseCannabisDosage: false,
    };
  }

  const thcUnit = additionalData.thc_unit || "%";
  const cbdUnit = additionalData.cbd_unit || "%";
  
  const thcDisplay = formatCannabisValueWithUnit(additionalData.thc, thcUnit);
  const cbdDisplay = formatCannabisValueWithUnit(additionalData.cbd, cbdUnit);
  
  // Only show strain if it's not empty
  const strain = (additionalData.strain && additionalData.strain.trim() !== "") 
    ? `Sorte: ${additionalData.strain}` : "";

  return {
    thcDisplay,
    cbdDisplay,
    strain,
    shouldUseCannabisDosage: true,
  };
};

export const parseRangeStr = (rangeStr: string) => {
  const result: { min: number | null; max: number | null } = { min: null, max: null };

  // Match 'gte' and 'lte' values
  const matches = rangeStr.match(/(gte|lte)(\d+(\.\d+)?)/g);

  if (matches) {
    matches.forEach(match => {
      const [operator, value] = match.split(/(\d+)/).filter(Boolean);
      if (operator === 'gte') {
        result.min = parseFloat(value);
      } else if (operator === 'lte') {
        result.max = parseFloat(value);
      }
    });
  }
  return result;
}


export const isInRange = (min: number | null, max: number | null, val: any): boolean => {
  if (typeof val !== "number") return false;
  return (min === null || val >= min) && (max === null || val <= max);
}


export const parseRangeFilter = (rangeStr: string): (val: any) => boolean => {
  const { min, max } = parseRangeStr(rangeStr);
  return (val: any) => isInRange(min, max, val);
}


export const sortRangeFilter = <T extends { value: string }>(items: T[]): T[] => {
  return items.sort((a, b) => {
    const rangeA = parseRangeStr(a.value);
    const rangeB = parseRangeStr(b.value);

    // Sort by min value (default to 0 if undefined)
    const minA = rangeA.min ?? 0;
    const minB = rangeB.min ?? 0;

    return minA - minB; // Ascending order based on min value
  });
}


export const getMinPricePerQuantity = (product: ProductWithStandardItems) => {
  let minPrice = Infinity;
  let quantity = 1;

  product?.items.forEach((productItem: any) => {
    if ((productItem.official_price / productItem?.quantity) < (minPrice / quantity)) {
      minPrice = productItem?.official_price;
      quantity = productItem?.quantity;
    }
  });
  return Number((minPrice / quantity).toFixed(2));
}


export const getMinPricePerProduct = (product: ProductWithStandardItems) => {
  let minPrice = Infinity;

  product?.items.forEach((productItem: any) => {
    if (productItem?.price < minPrice) {
      minPrice = productItem?.price;
    }
  });
  return Number(minPrice.toFixed(2));
}

const officialPriceOrZero = (item: ChooseTreatmentProductItemType) => item.official_price ?? 0;

export const minPriceForProduct = (product: ProductWithStandardItems) => {
  return product.items.reduce((acc, currItem) => {
    const basePrice = currItem.shipping_method_prices?.default ?? officialPriceOrZero(currItem);
    const price = basePrice / currItem.quantity
    return Math.min(acc, price)
  }, Infinity)
}

export const minPriceForProductSelfPickup = (product: ProductWithStandardItems) => {
  return product.items.reduce((acc, currItem) => {
    const basePrice = currItem.shipping_method_prices?.self_pickup ?? officialPriceOrZero(currItem);
    const price = basePrice / currItem.quantity
    return Math.min(acc, price)
  }, Infinity)
}

export const pricesForSelectedShippingMethod = (params: {
  product: ProductWithStandardItems,
  productItem: ChooseTreatmentProductItemType,
  shippingMethod: string,
  stockCodesInRange: string[],
  selectedStockCode: string | null,
}): { packPrice: number, minPricePerUnit: number } => {
  const { product, productItem, shippingMethod, stockCodesInRange, selectedStockCode } = params;
  const officialPrice = officialPriceOrZero(productItem);

  if (shippingMethod === "pickup") {
    const cheapestSelfPickupPrice = productItem.shipping_method_prices?.self_pickup ?? officialPrice;
    const packPrice = selectedStockCode
      ? officialPriceForProductItemSelfPickup(productItem, selectedStockCode)
      : cheapestSelfPickupPrice;
    return { packPrice, minPricePerUnit: minPriceForProductSelfPickup(product) };
  }

  if (shippingMethod === "express_shipping") {
    const expressPrice = officialPriceForProductItemExpress(productItem, stockCodesInRange, selectedStockCode || "");
    return {
      packPrice: expressPrice > 0 ? expressPrice : officialPrice,
      minPricePerUnit: Number(minPriceForProductExpress(product, stockCodesInRange, selectedStockCode || "")),
    };
  }

  return {
    packPrice: productItem.shipping_method_prices?.default ?? officialPrice,
    minPricePerUnit: minPriceForProduct(product),
  };
}

export const minPriceForProductExpress = (product: ProductWithStandardItems, stockCodesInRange: string[], selectedStockCode: string) => {

  // If a stock code is selected, only consider that stock
  if (selectedStockCode) {
    stockCodesInRange = stockCodesInRange.filter(stockCode => stockCode === selectedStockCode);
  }

  return product.items.reduce((acc, currItem) => {
    if (!currItem.express_stock) {
      return acc;
    }
    let minOfficialPriceExpress = 0;
    for (const stockCode of stockCodesInRange) {
      const stock = currItem.available_stocks?.find(s => s.stockCode === stockCode);
      if (stock && stock.officialPrice > 0) {
        minOfficialPriceExpress = stock.officialPrice;
        break;
      }
    }

    const basePrice = minOfficialPriceExpress;
    const price = basePrice / currItem.quantity
    return Math.min(acc, price)
  }, Infinity).toFixed(2)
}

export const officialPriceForProductItemExpress = (productItem: ChooseTreatmentProductItemType, stockCodesInRange: string[], selectedStockCode: string) => {

  // If a stock code is selected, only consider that stock
  if (selectedStockCode) {
    stockCodesInRange = stockCodesInRange.filter(stockCode => stockCode === selectedStockCode);
  }

  let minOfficialPriceExpress = 0;
  for (const stockCode of stockCodesInRange) {
    const stock = productItem.available_stocks?.find(s => s.stockCode === stockCode);
    if (stock && stock.officialPrice > 0) {
      minOfficialPriceExpress = stock.officialPrice;
      break;
    }
  }

  return minOfficialPriceExpress;
}


export const officialPriceForProductItemSelfPickup = (productItem: ChooseTreatmentProductItemType, selectedStockCode: string) => {
  const stockPrice = productItem?.available_stocks?.find(stock => stock.stockCode === selectedStockCode)?.officialPrice;
  if (stockPrice && stockPrice > 0) {
    return stockPrice;
  }
  return productItem.official_price;
}

export const medicationLinePrice = (
  productItem: ChooseTreatmentProductItemType & { selected_stock_code?: string | null },
  shippingMethod: string,
  selectedStockCode?: string | null,
): number => {
  if (shippingMethod === 'express_shipping') {
    const selectedStockPrice = selectedStockCode
      ? productItem.available_stocks?.find((stock) => stock.stockCode === selectedStockCode)?.officialPrice
      : undefined;
    return (selectedStockPrice && selectedStockPrice > 0)
      ? selectedStockPrice
      : productItem.official_price;
  }
  if (shippingMethod === 'pickup') {
    const pickupStockCode = productItem.selected_stock_code || selectedStockCode;
    const selectedStockPrice = pickupStockCode
      ? productItem.available_stocks?.find((stock) => stock.stockCode === pickupStockCode)?.officialPrice
      : undefined;
    if (selectedStockPrice && selectedStockPrice > 0) {
      return selectedStockPrice;
    }
    return productItem.shipping_method_prices?.self_pickup ?? productItem.official_price;
  }
  return productItem.shipping_method_prices?.default ?? productItem.official_price;
}

/**
 * Calculate the visible minimum price per unit that is actually displayed to the user.
 * This function encapsulates the price display logic based on shipping method and stock selection.
 * 
 * @param product - The product to calculate price for
 * @param shippingMethod - Current shipping method ('shipping', 'express_shipping', or 'pickup')
 * @param stockCodesInRange - Array of stock codes in range for express shipping
 * @param selectedStockCode - Currently selected stock code (for pickup or express)
 * @param desiredProductItem - The desired product item (if user has selected one)
 * @returns The visible price per unit
 */
export const visibleMinPricePerQuantity = (
  product: ProductWithStandardItems,
  shippingMethod: string,
  stockCodesInRange: string[],
  selectedStockCode: string | null,
  desiredProductItem?: any
): number => {
  const EXPRESS_SHIPPING = 'express_shipping';
  const PICKUP = 'pickup';
  
  if (shippingMethod === PICKUP) {
    const hasSelfPickupStock = desiredProductItem?.selected_stock_code || selectedStockCode;
    if (hasSelfPickupStock) {
      const stockCode = desiredProductItem?.selected_stock_code || selectedStockCode || '';
      const productItem = desiredProductItem || product.items[0];
      const selfPickupPrice = officialPriceForProductItemSelfPickup(productItem, stockCode);
      return selfPickupPrice / productItem.quantity;
    }
    return minPriceForProductSelfPickup(product);
  }
  
  // Case 2: Express shipping
  if (shippingMethod === EXPRESS_SHIPPING) {
    const expressPrice = officialPriceForProductItemExpress(desiredProductItem || product.items[0], stockCodesInRange, selectedStockCode || '');
    const expressPricePerUnit = expressPrice / (desiredProductItem?.quantity || product.items[0].quantity);
    
    if (expressPricePerUnit > 0) {
      return expressPricePerUnit;
    }
  }
  
  // Case 3: Regular shipping (default)
  return minPriceForProduct(product);
}

/**
 * Calculate price per gram from price per unit and product item
 * @param pricePerUnit - The price per unit (e.g., price for the entire product item)
 * @param productItem - The product item containing quantity information
 * @param quantityType - The quantity type from the product ('gram' or other)
 * @returns Price per gram or null if calculation is not possible
 */
export const calculatePricePerGram = (
  pricePerUnit: number,
  productItem: ChooseTreatmentProductItemType,
  quantityType?: string
): number | null => {
  if (!pricePerUnit || pricePerUnit <= 0 || !productItem) {
    return null;
  }

  const customQuantityTitle = productItem?.custom_quantity_title || '';
  
  // Try to extract grams from custom_quantity_title (e.g., "10 g", "5 Gramm")
  const gramMatch = customQuantityTitle.match(/(\d+(?:\.\d+)?)\s*(?:g|gram|gramm)/i);
  if (gramMatch) {
    const grams = parseFloat(gramMatch[1]);
    if (grams > 0) {
      return pricePerUnit / grams;
    }
  }
  
  // Fallback: if quantity_type is 'gram', use quantity
  if (quantityType === 'gram' && productItem?.quantity > 0) {
    return pricePerUnit / productItem.quantity;
  }
  
  return null;
}

/**
 * Get the product price for a specific stock code from the selected product
 * @param selectedProduct - The selected product from context (Product | null | undefined)
 * @param stockCode - The stock code to find the price for
 * @returns The official price for the stock code or null if not found
 */
export const getProductPriceForStock = (
  selectedProduct: { items: Array<{ available_stocks?: Array<{ stockCode: string; officialPrice: number }> }> } | null | undefined,
  stockCode: string
): number | null => {
  if (!selectedProduct) {
    return null;
  }

  const productItem = selectedProduct.items[0];
  if (!productItem) {
    return null;
  }

  const stockPrice = productItem?.available_stocks?.find(stock => stock.stockCode === stockCode)?.officialPrice;
  if (stockPrice && stockPrice > 0) {
    return stockPrice;
  }

  return null;
}

/**
 * @deprecated This function is deprecated.
 * Please use {@link translator.getPrice} instead (returns also currency sign).
 */
export const toLocalePrice = (price: number, locale = 'de-DE') => {
  if (!price) return "";
  return price.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}


export const getMaxPricePerQuantity = (product: ProductWithStandardItems) => {
  let maxPrice = 0;
  let quantity = 1;

  product?.items.forEach((productItem: any) => {
    if ((productItem.price / productItem?.quantity) > (maxPrice / quantity)) {
      maxPrice = productItem?.price;
      quantity = productItem?.quantity;
    }
  })

  return toLocalePrice(maxPrice / quantity);
}




export const sortProducts = <T extends ProductWithStandardItems>(
  products: T[],
  sortByFn: (p1: T, p2: T) => number
): T[] => {
  // always prioritize premium before regular
  return [...products].sort((p1, p2) => {
    // Put in-stock products first
    if (p1.in_stock !== p2.in_stock) {
      return p1.in_stock ? -1 : 1;
    }
    // If both are in the same stock state, use custom sort
    return sortByFn(p1, p2);
  });
};

export const productedSortedByPriceAsc = <T extends ProductWithStandardItems>(products: T[]): T[] => sortProducts(products, (p1, p2) => {
  // First prioritize gram vs non-gram products
  if (p1.quantity_type === 'gram' && p2.quantity_type !== 'gram') return -1;
  if (p1.quantity_type !== 'gram' && p2.quantity_type === 'gram') return 1;

  // Then apply price sorting using visiblePrice
  if (!p1.in_stock && !p2.in_stock) return 0;
  
  const p1Price = (p1 as any).visiblePrice ?? p1.minPricePerQuantity;
  const p2Price = (p2 as any).visiblePrice ?? p2.minPricePerQuantity;
  
  return p1Price - p2Price;
});

export const productedSortedByPriceDesc = <T extends ProductWithStandardItems>(products: T[]): T[] => sortProducts(products, (p1, p2) => {
  // First prioritize gram vs non-gram products
  if (p1.quantity_type === 'gram' && p2.quantity_type !== 'gram') return -1;
  if (p1.quantity_type !== 'gram' && p2.quantity_type === 'gram') return 1;

  // Then apply price sorting using visiblePrice
  if (!p1.in_stock && !p2.in_stock) return 0;
  
  const p1Price = (p1 as any).visiblePrice ?? p1.minPricePerQuantity;
  const p2Price = (p2 as any).visiblePrice ?? p2.minPricePerQuantity;
  
  return p2Price - p1Price;
});

export const productedSortedByThcAsc = <T extends ProductWithStandardItems>(products: T[]): T[] => sortProducts(products, (p1, p2) => {
  // First prioritize gram vs non-gram products
  if (p1.quantity_type === 'gram' && p2.quantity_type !== 'gram') return -1;
  if (p1.quantity_type !== 'gram' && p2.quantity_type === 'gram') return 1;

  // Then apply regular THC sorting
  return (p1.additional_data?.thc ?? 0) - (p2.additional_data?.thc ?? 0);
});

export const productedSortedByThcDesc = <T extends ProductWithStandardItems>(products: T[]): T[] => sortProducts(products, (p1, p2) => {
  // First prioritize gram vs non-gram products
  if (p1.quantity_type === 'gram' && p2.quantity_type !== 'gram') return -1;
  if (p1.quantity_type !== 'gram' && p2.quantity_type === 'gram') return 1;

  // Then apply regular THC sorting
  return (p2.additional_data?.thc ?? 0) - (p1.additional_data?.thc ?? 0);
});

export const getOrderRateViolation = (cannabisLimits: CannabisLimitsResult | null): CannabisLimitViolation | undefined => {
  return cannabisLimits?.violations.find(v => v.type === 'hourly_order' || v.type === 'daily_order');
};

export const isOrderRateLimited = (cannabisLimits: CannabisLimitsResult | null): boolean => {
  return !!getOrderRateViolation(cannabisLimits);
};

export const getCannabisLimitMessage = (cannabisLimits: CannabisLimitsResult | null, language: string, translationCategory: string = 'choose_treatment'): string => {
  if (!cannabisLimits?.violations?.length) return '';
  const orderRateViolation = getOrderRateViolation(cannabisLimits);
  if (orderRateViolation) {
    const key = orderRateViolation.type === 'hourly_order' ? 'cannabis_limits/hourly_order_limit' : 'cannabis_limits/daily_order_limit';
    return translate(language, translationCategory, key).replace('<time>', String(orderRateViolation.retryAfterHours));
  }
  const gramsViolation = cannabisLimits.violations.find(v => v.type === 'monthly_grams');
  if (gramsViolation) {
    return translate(language, translationCategory, 'cannabis_limits/monthly_grams_limit');
  }
  return '';
};

type FilterDataItem = { value: string; isSelected: boolean; name: string; keyWordTitle: string; filterFn: (product: any) => boolean };
type FilterData = Record<string, FilterDataItem[]>;

const SORT_BY_MAP: Record<string, string> = {
  [SORT_BY_PRICE_ASC]: "price_asc",
  [SORT_BY_PRICE_DESC]: "price_desc",
  [SORT_BY_THC_ASC]: "thc_asc",
  [SORT_BY_THC_DESC]: "thc_desc",
};

export const serializeFiltersForServer = (
  filterData: FilterData | undefined,
  searchTerm: string,
  sortBy: SortByType,
  excludeProductIds?: number[],
  favoriteCatalogIds?: number[],
  shippingMethod?: string,
  purchaseTier?: string,
  expressStockCodes?: string[],
  selectedStockCode?: string | null,
  externalPharmacyIds?: string[],
  availablePharmacyStockCodes?: string[],
): Record<string, any> => {
  const params: Record<string, any> = {};

  if (searchTerm) {
    params.search_term = searchTerm;
  }

  if (sortBy) {
    params.sort_by = SORT_BY_MAP[sortBy] || undefined;
  }

  if (excludeProductIds?.length) {
    params.exclude_product_ids = excludeProductIds;
  }

  // favorite_catalog_ids is a hard filter server-side, so it is only sent when the user has
  // actually checked the "favourites" filter — see the gated block further down. Sending it
  // unconditionally here restricted every page load to the user's favorites.

  if (!filterData) return params;

  const selected = (key: string) =>
    filterData[key]?.filter((f) => f.isSelected).map((f) => f.value) ?? [];

  const thcValues = selected("thc");
  if (thcValues.length) {
    params.thc_ranges = thcValues.map((v) => {
      const { min, max } = parseRangeStr(v);
      const range: Record<string, number> = {};
      if (min != null) range.gte = min;
      if (max != null) range.lte = max;
      return range;
    });
  }

  const cbdValues = selected("cbd");
  if (cbdValues.length) {
    params.cbd_ranges = cbdValues.map((v) => {
      const { min, max } = parseRangeStr(v);
      const range: Record<string, number> = {};
      if (min != null) range.gte = min;
      if (max != null) range.lte = max;
      return range;
    });
  }

  const geneticValues = selected("genetic").filter((v) => v !== "all");
  if (geneticValues.length) {
    params.genetic = geneticValues;
  }

  const effectsValues = selected("effects");
  if (effectsValues.length) {
    params.effects = effectsValues;
  }

  const medicalValues = selected("medical_effects");
  if (medicalValues.length) {
    params.medical_attributes = medicalValues;
  }

  const typeValues = selected("type");
  if (typeValues.length) {
    params.cannabis_type = typeValues;
  }

  const priceValues = selected("price");
  if (priceValues.length) {
    params.price_per_quantity_ranges = priceValues.map((v) => {
      const { min, max } = parseRangeStr(v);
      const range: Record<string, number> = {};
      if (min != null) range.gte = min;
      if (max != null) range.lte = max;
      return range;
    });
  }

  const favouriteSelected = filterData["favourite"]?.some((f) => f.isSelected);
  if (favouriteSelected && favoriteCatalogIds?.length) {
    params.favorite_catalog_ids = favoriteCatalogIds;
  }

  if (shippingMethod) {
    params.shipping_method = shippingMethod;
  }

  if (purchaseTier) {
    params.purchase_tier = purchaseTier;
  }

  if (expressStockCodes?.length) {
    params.express_stock_codes = expressStockCodes;
  }

  if (selectedStockCode) {
    params.selected_stock_code = selectedStockCode;
  }

  if (externalPharmacyIds?.length) {
    params.external_pharmacy_ids = externalPharmacyIds;
  }

  if (availablePharmacyStockCodes?.length) {
    params.available_pharmacy_stock_codes = availablePharmacyStockCodes;
  }

  return params;
};